import { Component, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthServiceLocal } from 'src/@order/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderData } from '../get-qr-order-details/get-qr-order-details.component';
import { MatDialog } from '@angular/material/dialog';
import { AssignRiderComponent } from '../auth/assign-rider/assign-rider.component';

@Component({
  selector: 'app-distribution-partner',
  templateUrl: './distribution-partner.component.html',
  styleUrls: ['./distribution-partner.component.scss'],
})
export class DistributionPartnerComponent implements OnInit {
  private destroy$ = new Subject<void>();

  data: OrderData = {
    weightClass: '',
    deliveryPlan: '',
    securityCover: '',
    phlenuTrustCheck: '',
    packageContent: '',
    orderId: '',
    orderStatus: '',
  };

  orderDetail: any = {};
  dropDetail: any = {};
  orderId: string = '';
  token: string = '';
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  isUpdatingStatus: boolean = false;

  // Status options for dropdown
  statusOptions = [
    { value: 'processing', label: 'Processing' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'recipient_re_scheduled', label: 'Recipient Re-Scheduled' },
    { value: 'held_at_distribution_site', label: 'Held at Distribution Site' },
    // { value: 'returned', label: 'Returned' }
  ];

  selectedStatus: string = '';
  remarks: string = '';

  // Helper function to get currency symbol based on country
  getCurrencySymbol(country: string): string {
    switch (country?.toLowerCase()) {
      case 'nigeria':
        return '₦';
      case 'ghana':
        return '₵';
      default:
        return '₵'; // Default to Ghanaian Cedi
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthServiceLocal,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Get order ID from route parameters and token from query parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.token = queryParams['token'] || '';

        console.log('token', this.token);

        if (this.token) {
          this.loadOrderData();
        } else {
          this.hasError = true;
          this.errorMessage = 'Token is required';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadOrderData(): void {
    if (!this.token) {
      this.hasError = true;
      this.errorMessage = 'Token is required';
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.authService
      .getDsOrderDetails({ token: this.token })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response && response.response === 200) {
            this.mapApiResponseToData(response.data);
          } else {
            this.hasError = true;
            this.errorMessage =
              response?.message || 'Failed to load order details';
            this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.hasError = true;
          this.errorMessage = 'Error loading order details. Please try again.';
          console.error('API Error:', error);
          this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
        },
      });
  }

  private mapApiResponseToData(apiData: any): void {
    // Map API response to component data structure
    const orderDetail = apiData.orderDetail;
    const dropDetail = apiData.dropDetail;

    // Set the raw data for template access
    this.orderDetail = orderDetail || {};
    this.dropDetail = dropDetail || {};

    // Map order status from numeric to string
    const getStatusString = (status: number): string => {
      switch (status) {
        case 1:
          return 'processing';
        case 2:
          return 'in_transit';
        case 3:
          return 'delivered';
        case 4:
          return 'canceled';
        case 5:
          return 'recipient_re_scheduled';
        case 6:
          return 'held_at_distribution_site';
        // case 7: return 'returned';
        default:
          return 'processing';
      }
    };

    // Map delivery plan from numeric to string
    const getDeliveryPlanString = (plan: number): string => {
      switch (plan) {
        case 1:
          return 'Express';
        case 2:
          return 'Regular';
        case 3:
          return 'Saver';
        default:
          return 'Regular';
      }
    };

    // Map package size from numeric to string
    const getPackageSizeString = (size: number): string => {
      switch (size) {
        case 1:
          return 'Small';
        case 2:
          return 'Medium';
        case 3:
          return 'Large';
        case 4:
          return 'Extra Large';
        default:
          return 'Medium';
      }
    };

    console.log('orderDetail', orderDetail);
    this.data = {
      orderId: orderDetail?.id?.toString() || '',
      weightClass:
        getPackageSizeString(dropDetail?.packageSize) || 'Not Available',
      deliveryPlan:
        getDeliveryPlanString(dropDetail?.deliveryPlan) || 'Not Available',
      securityCover: dropDetail?.isCoverApplied ? 'Applied' : 'Not Applied',
      phlenuTrustCheck: dropDetail?.isPhlenuTrustCheckApplied
        ? 'Applied'
        : 'Not Applied',
      packageContent: dropDetail?.sendingItemName || 'Not Available',
      orderStatus: getStatusString(dropDetail?.dropStatus) || 'processing',
    };

    // Set the selected status for the dropdown
    this.selectedStatus = this.data.orderStatus;
    this.orderId = orderDetail?.id || '';
  }

  public updateOrderStatus(): void {
    console.log('selectedStatus', this.selectedStatus);
    console.log('orderId', this.orderId);
    console.log('token', this.token);
    console.log('remarks', this.remarks);

    if (!this.selectedStatus || !this.orderId) {
      this.snackBar.open(
        'Please select a status and ensure order ID is available',
        'Close',
        { duration: 3000 }
      );
      return;
    }

    if (!this.token) {
      this.snackBar.open('Token is required for status update', 'Close', {
        duration: 3000,
      });
      return;
    }

    // Validate remarks for cancellation
    if (
      this.selectedStatus === 'canceled' &&
      (!this.remarks || this.remarks.trim() === '')
    ) {
      this.snackBar.open(
        'Remarks are required when canceling an order',
        'Close',
        { duration: 3000 }
      );
      return;
    }

    this.isUpdatingStatus = true;

    // Convert string status to numeric for API
    const getStatusNumber = (status: string): number => {
      switch (status) {
        case 'processing':
          return 1;
        case 'in_transit':
          return 2;
        case 'delivered':
          return 3;
        case 'canceled':
          return 4;
        case 'recipient_re_scheduled':
          return 5;
        case 'held_at_distribution_site':
          return 6;
        case 'returned':
          return 7;
        default:
          return 1;
      }
    };

    const updatePayload = {
      orderId: this.orderDetail?.id || this.orderId,
      dropId: this.dropDetail?.id,
      remarks: this.remarks || `Status updated to ${this.selectedStatus}`,
      orderStatus: getStatusNumber(this.selectedStatus),
      dsId:
        this.dropDetail?.selectedDropoffDS ||
        this.orderDetail?.selectedDropoffDS,
    };
    console.log('updatePayload', updatePayload);
    this.authService
      .updateDsOrderStatus(updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isUpdatingStatus = false;
          if (response && response.response === 1) {
            this.data.orderStatus = this.selectedStatus;
            this.snackBar.open('Order status updated successfully!', 'Close', {
              duration: 3000,
            });
          } else {
            this.snackBar.open(
              response?.message || 'Failed to update order status',
              'Close',
              { duration: 5000 }
            );
          }
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          console.error('Update Status Error:', error);
          this.snackBar.open(
            'Error updating order status. Please try again.',
            'Close',
            { duration: 5000 }
          );
        },
      });
  }
  openAssignDialog() {
    const dialogRef = this.dialog.open(AssignRiderComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Build payload
        const payload = {
          dropId: this.dropDetail?.id,
          orderId: this.orderDetail?.id || this.orderId,
          name: result.driverName,
          plateNumber: result.vehiclePlate,
          primaryNum: `${result.primaryCode}${result.primaryNumber}`,
          secondayNum: result.secondaryNumber
            ? `${result.secondaryCode}${result.secondaryNumber}`
            : null,
        };

        console.log('Payload:', payload);

        // Call API
        this.authService
          .assignRider(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              console.log('Rider assigned successfully:', res);
              this.snackBar.open('Rider assigned successfully:!', 'Close', {
                duration: 3000,
              });
            },
            error: (err) => {
              console.error('Error assigning rider:', err);
            },
          });
      }
    });
  }
}
